import { useCallback, useMemo, useState } from "react";
import { LoadingShim, formatNumber } from "@narmi/design_system";

import {
  CursorCoordinatesPropType,
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryCursorContainer,
  VictoryGroup,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip
} from "victory";
import filters from "byzantine/src/filters";
import useElementWidth from "../../hooks/useElementWidth";

import styles from "./BaseChart.module.scss";
import {
  TICK_LABEL_PX,
  MIN_TICK_FONT_SIZE,
  MAX_TICK_FONT_SIZE,
  TICK_LABEL_LINE_HEIGHT,
  GRID_STROKE_PX
} from "./constants";
import { TimePeriod, ChartDatum, ChartDomain, CursorValue } from "./types";
import GridLines from "./GridLines";
import {
  getTickValues,
  getGridLinePositions,
  getNearestDatum,
  formatDateTick
} from "./utils";

type BaseChartProps = {
  timePeriod: TimePeriod;
  chartData: ChartDatum[];
  windowStart: Date;
  windowEnd: Date;
  isLoading: boolean;
  isSmall: boolean;
  error: unknown;
};

const BaseChart = ({
  timePeriod,
  chartData,
  windowStart,
  windowEnd,
  isLoading,
  isSmall,
  error
}: BaseChartProps) => {
  const [cursorX, setCursorX] = useState<CursorValue>(null);
  const [containerRef, containerWidth] = useElementWidth();
  // These dimensions reflect the aspect ratio to use for the chart's generated SVG.
  const chartHeight = isSmall ? 300 : 222;
  const chartWidth = isSmall ? 720 : 1440;

  // Victory renders a responsive SVG (`viewBox` + `width: 100%`), so everything
  // inside is scaled by `containerWidth / chartWidth`. Multiplying by the
  // inverse converts a target pixel size into the user units that render at
  // that size. Falls back to 1:1 until the first measurement lands.
  const userUnitsPerPx = containerWidth > 0 ? chartWidth / containerWidth : 1;
  const tickFontSize = Math.min(
    Math.max(TICK_LABEL_PX * userUnitsPerPx, MIN_TICK_FONT_SIZE),
    MAX_TICK_FONT_SIZE
  );
  const bottomPadding = Math.round(tickFontSize * TICK_LABEL_LINE_HEIGHT);
  const gridStrokeWidth = GRID_STROKE_PX * userUnitsPerPx;

  const chartDomain = useMemo<ChartDomain | undefined>(() => {
    if (chartData.length === 0) return undefined;

    const balances = chartData.map(({ y }) => y);
    const minY = Math.min(...balances);
    const maxY = Math.max(...balances);
    const yRange = maxY - minY;
    const yPadding =
      yRange === 0 ? Math.max(Math.abs(maxY) * 0.05, 1) : yRange * 0.1;

    return {
      x: [windowStart, windowEnd] as [Date, Date],
      y: [minY - yPadding, maxY + yPadding] as [number, number]
    };
  }, [chartData, windowStart, windowEnd]);

  const tickValues = isSmall
    ? getTickValues(chartDomain, 6)
    : getTickValues(chartDomain);
  const gridLinePositions = getGridLinePositions(
    chartHeight,
    bottomPadding,
    gridStrokeWidth
  );
  const activeDatum = getNearestDatum(chartData, cursorX);

  const onCursorChange = useCallback((value: CursorCoordinatesPropType) => {
    if (
      value instanceof Date ||
      typeof value === "number" ||
      typeof value === "string"
    ) {
      setCursorX(value);
      return;
    }

    setCursorX(null);
  }, []);

  if (error) return null;

  return (
    <LoadingShim isLoading={isLoading}>
      {chartData.length > 0 ? (
        <div ref={containerRef} className={styles.chart}>
          <svg className={styles.svgDefs}>
            <defs>
              <linearGradient
                id="line-area-gradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" className={styles.areaGradientStart} />
                <stop offset="100%" className={styles.areaGradientEnd} />
              </linearGradient>
              <linearGradient
                id="cursor-line-gradient"
                x1="0"
                y1="0"
                x2="0"
                y2={chartHeight}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" className={styles.cursorGradientStart} />
                <stop offset="50%" className={styles.cursorGradientStart} />
                <stop offset="100%" className={styles.cursorGradientEnd} />
              </linearGradient>
            </defs>
          </svg>

          <VictoryChart
            height={chartHeight}
            width={chartWidth}
            style={{
              background: {
                fill: "var(--base-chart-background-fill)"
              }
            }}
            padding={{ top: 40, bottom: bottomPadding, left: 0, right: 0 }}
            domain={chartDomain}
            scale={{ x: "time" }}
            containerComponent={
              <VictoryCursorContainer
                cursorDimension="x"
                // We use this component to track cursor location
                cursorComponent={<div />}
                onCursorChange={onCursorChange}
              />
            }
          >
            <VictoryAxis
              orientation="bottom"
              offsetY={bottomPadding}
              tickValues={tickValues}
              tickFormat={(tick) => formatDateTick(tick, timePeriod)}
              style={{
                tickLabels: {
                  fontWeight: "var(--base-chart-axis-font-weight)",
                  textAnchor: "middle",
                  fontSize: tickFontSize,
                  fontFamily: "var(--base-chart-axis-font-family)",
                  fill: "var(--base-chart-axis-color)"
                },
                axis: {
                  stroke: "var(--base-chart-axis-stroke)"
                }
              }}
            />
            <VictoryGroup data={chartData} y={(datum) => datum.y}>
              <VictoryArea
                y0={() => chartDomain?.y[0] ?? 0}
                style={{ data: { fill: "url(#line-area-gradient)" } }}
                interpolation="catmullRom"
              />
              <VictoryLine
                style={{
                  data: {
                    stroke: "var(--base-chart-halo-stroke)",
                    strokeWidth: 9
                  }
                }}
                interpolation="catmullRom"
              />
              <VictoryLine
                style={{
                  data: {
                    stroke: "var(--base-chart-line-stroke)",
                    strokeWidth: 2
                  }
                }}
                interpolation="catmullRom"
              />
            </VictoryGroup>
            {activeDatum && chartDomain && (
              <VictoryLine
                data={[
                  { x: activeDatum.x, y: activeDatum.y },
                  { x: activeDatum.x, y: chartDomain.y[0] }
                ]}
                style={{
                  data: {
                    stroke: "url(#cursor-line-gradient)",
                    strokeWidth: 3,
                    strokeDasharray: "var(--base-chart-cursor-dasharray)",
                    strokeLinecap: "var(--base-chart-cursor-linecap)"
                  }
                }}
              />
            )}
            {activeDatum && (
              <VictoryScatter
                data={[activeDatum]}
                labels={({ datum }) => [
                  `${formatNumber(datum.y, "currency")}`,
                  filters.shortMonthDayYear(datum.x)
                ]}
                labelComponent={
                  <VictoryTooltip
                    active
                    constrainToVisibleArea
                    orientation="top"
                    pointerOrientation="bottom"
                    pointerLength={0}
                    pointerWidth={0}
                    // Lifts tooltip above the scatter point
                    centerOffset={{ y: -20 }}
                    flyoutStyle={{
                      fill: "var(--base-chart-tooltip-fill)",
                      filter: "var(--base-chart-tooltip-shadow)"
                    }}
                    flyoutPadding={{ top: 6, bottom: 6, left: 10, right: 10 }}
                    style={[
                      {
                        fill: "var(--base-chart-tooltip-label-color)",
                        textAnchor: "start",
                        fontSize: tickFontSize
                      },
                      {
                        fill: "var(--base-chart-tooltip-sublabel-color)",
                        textAnchor: "start",
                        fontSize: tickFontSize
                      }
                    ]}
                  />
                }
                size={4}
                style={{ data: { fill: "var(--base-chart-point-fill)" } }}
              />
            )}
            <GridLines
              linePositions={gridLinePositions}
              chartWidth={chartWidth}
              strokeWidth={gridStrokeWidth}
            />
          </VictoryChart>
        </div>
      ) : (
        <div className={styles.emptyState} />
      )}
    </LoadingShim>
  );
};

export default BaseChart;
